import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Card, Typography, Tag, Progress, Divider, Button, Collapse, message, ConfigProvider, Grid, Result } from 'antd';
import { CopyOutlined, ShareAltOutlined, StarFilled, FacebookFilled, InstagramFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { calculateWuxing, WUXING_EMOJI, WUXING_COLORS } from '../utils/wuxingCalculator';
import { calculateNumerology, NUMBER_MEANINGS } from '../utils/numerologyCalculator';
import { getWuxingRecommendation, getNumerologyRecommendation, WUXING_COLOR_MAP } from '../utils/colorRecommendation';
import { decodeInsight } from '../utils/insightCode';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const COLOR_HEX_MAP = {
  '紅色': '#f5222d', '橙色': '#fa8c16', '黃色': '#fadb14',
  '綠色': '#52c41a', '藍色': '#1890ff', '紫色': 'var(--brand-primary)',
  '粉色': '#eb2f96', '黑色': '#333', '白色': '#d9d9d9',
  '金色': '#d4a017', '銀色': '#a0a0a0', '棕色': '#8b4513',
  '青色': '#13c2c2', '深紅': '#a8071a',
};
const LIGHT_COLORS = new Set(['白色', '黃色', '金色', '銀色']);

// 命理對照（專業細節用）
const TIANGAN_EL = { '甲': '木', '乙': '木', '丙': '火', '丁': '火', '戊': '土', '己': '土', '庚': '金', '辛': '金', '壬': '水', '癸': '水' };
const TIANGAN_YINYANG = { '甲': '陽', '丙': '陽', '戊': '陽', '庚': '陽', '壬': '陽', '乙': '陰', '丁': '陰', '己': '陰', '辛': '陰', '癸': '陰' };
const DIZHI_EL = { '子': '水', '丑': '土', '寅': '木', '卯': '木', '辰': '土', '巳': '火', '午': '火', '未': '土', '申': '金', '酉': '金', '戌': '土', '亥': '水' };
const EL_GEN = { '木': '水', '火': '木', '土': '火', '金': '土', '水': '金' };  // 生我者
const WUXING_INFO = {
  '木': { dir: '東方', season: '春季', num: '3、8', career: '文教、設計、企劃、園藝、出版' },
  '火': { dir: '南方', season: '夏季', num: '2、7', career: '行銷、餐飲、表演、3C、美容' },
  '土': { dir: '中央 / 四隅', season: '四季月', num: '5、0', career: '房地產、農業、管理、顧問、保險' },
  '金': { dir: '西方', season: '秋季', num: '4、9', career: '金融、機械、法律、珠寶、軍警' },
  '水': { dir: '北方', season: '冬季', num: '1、6', career: '貿易、物流、媒體、旅遊、流動服務' },
};

const colorTagStyle = (c) => {
  const bg = COLOR_HEX_MAP[c];
  if (!bg) return {};
  return { background: bg, color: LIGHT_COLORS.has(c) ? '#333' : '#fff', border: c === '白色' ? '1px solid #d9d9d9' : 'none' };
};

// 五行雷達圖（純 SVG）
function WuxingRadar({ scores, icons = {} }) {
  const W = 300, H = 280, cx = W / 2, cy = H / 2, R = 76, labelR = R + 22;
  const els = ['木', '火', '土', '金', '水'];
  const n = els.length;
  const maxVal = Math.max(...els.map((e) => scores[e] || 0)) || 1;
  const angle = (i) => (-90 + i * (360 / n)) * (Math.PI / 180);
  const point = (i, r) => [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  const polyAt = (f) => els.map((_, i) => point(i, R * f).join(',')).join(' ');
  const dataPoly = els.map((e, i) => point(i, R * ((scores[e] || 0) / maxVal)).join(',')).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: 340, display: 'block', margin: '0 auto' }}>
      <defs>
        <linearGradient id="radarStroke" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#949DD6" />
          <stop offset="50%" stopColor="#6B77C7" />
          <stop offset="100%" stopColor="#eb6fb0" />
        </linearGradient>
        <radialGradient id="radarFill" cx="50%" cy="45%" r="65%">
          <stop offset="0%" stopColor="rgba(107,119,199,0.28)" />
          <stop offset="100%" stopColor="rgba(148,157,214,0.08)" />
        </radialGradient>
        <filter id="radarGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#6B77C7" floodOpacity="0.35" />
        </filter>
      </defs>
      {/* 同心網格：柔紫細線 */}
      {[0.25, 0.5, 0.75, 1].map((f, idx) => <polygon key={f} points={polyAt(f)} fill="none" stroke="#EAECF6" strokeWidth={idx === 3 ? 1.4 : 1} />)}
      {els.map((_, i) => { const [x, y] = point(i, R); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#EAECF6" strokeWidth={1} />; })}
      {/* 相剋五芒星：外圈相鄰=相生，星線=相剋 */}
      {els.map((_, i) => { const [x1, y1] = point(i, R); const [x2, y2] = point((i + 2) % n, R); return <line key={`k${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D9DCF0" strokeWidth={0.8} strokeDasharray="2 4" strokeLinecap="round" />; })}
      {/* 柔光暈：緩慢明滅 */}
      <polygon points={dataPoly} fill="url(#radarStroke)" opacity={0.18} style={{ animation: 'radarSheen 4s ease-in-out infinite', filter: 'blur(6px)' }} />
      <polygon points={dataPoly} fill="url(#radarFill)" stroke="url(#radarStroke)" strokeWidth={2.6} strokeLinejoin="round" filter="url(#radarGlow)" style={{ animation: 'radarBreath 3.5s ease-in-out infinite' }} />
      {els.map((e, i) => {
        const [x, y] = point(i, R * ((scores[e] || 0) / maxVal));
        return (
          <g key={e}>
            {/* 頂點漣漪光暈 */}
            <circle cx={x} cy={y} r={4} fill="none" stroke="url(#radarStroke)" strokeWidth={1.5} style={{ animation: `radarPing 2.8s ease-out ${i * 0.4}s infinite` }} />
            <circle cx={x} cy={y} r={4} fill="#fff" stroke="url(#radarStroke)" strokeWidth={2.2} />
          </g>
        );
      })}
      {els.map((e, i) => {
        const [lx, ly] = point(i, labelR);
        const cos = Math.cos(angle(i));
        const anchor = Math.abs(cos) < 0.3 ? 'middle' : cos > 0 ? 'start' : 'end';
        const icon = icons[e];
        const IS = 16; // icon size
        // 有圖：圖示置於頂點外側，文字緊接其下；無圖：退回 emoji
        if (icon) {
          return (
            <g key={e}>
              <image href={icon} x={lx - IS / 2} y={ly - IS - 4} width={IS} height={IS} preserveAspectRatio="xMidYMid meet" />
              <text x={lx} y={ly + 9} textAnchor="middle" dominantBaseline="middle" fontSize={13} fill="#595959">
                <tspan fontWeight={600}>{e}</tspan>
                <tspan dx={2} fill="#222957">{scores[e]}</tspan>
              </text>
            </g>
          );
        }
        return (
          <text key={e} x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" fontSize={13} fill="#595959">
            <tspan fontSize={15}>{WUXING_EMOJI[e]}</tspan>
            <tspan dx={2} fontWeight={600}>{e}</tspan>
            <tspan dx={2} fill="#222957">{scores[e]}</tspan>
          </text>
        );
      })}
    </svg>
  );
}

// L 形轉角藤蔓 — 從角落沿著上緣與側緣各長一段，貼著邊界生長
function CornerVine({ position = 'top-left', size = 150, color = 'var(--brand-primary)', opacity = 0.14 }) {
  const flipX = position.includes('right');
  const flipY = position.includes('bottom');
  const pos = {
    position: 'absolute', width: size, height: size, pointerEvents: 'none', zIndex: 0,
    [flipY ? 'bottom' : 'top']: -4, [flipX ? 'right' : 'left']: -4,
    transform: `${flipX ? 'scaleX(-1)' : ''} ${flipY ? 'scaleY(-1)' : ''}`.trim() || 'none',
    transformOrigin: 'center',
    opacity,
  };
  // 以左上角為基準繪製；其他角靠 scale 翻轉
  return (
    <svg viewBox="0 0 150 150" style={pos} aria-hidden="true">
      <g fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round">
        {/* 主藤：沿上緣再轉向沿左緣 */}
        <path d="M132 9 C 92 5, 50 7, 26 11 C 13 14, 10 24, 9 38 C 8 70, 9 104, 11 132" />
        {/* 沿途分枝 */}
        <path d="M104 8 C 101 17, 103 24, 110 28" />
        <path d="M66 8 C 63 18, 66 25, 73 29" />
        <path d="M9 56 C 19 53, 26 56, 30 64" />
        <path d="M10 94 C 20 91, 27 94, 31 102" />
      </g>
      <g fill={color}>
        {/* 葉片（細長水滴），沿主藤與分枝分布 */}
        <ellipse cx="113" cy="29" rx="7" ry="3.2" transform="rotate(28 113 29)" />
        <ellipse cx="106" cy="16" rx="6.5" ry="3" transform="rotate(-32 106 16)" />
        <ellipse cx="76" cy="30" rx="7" ry="3.2" transform="rotate(34 76 30)" />
        <ellipse cx="68" cy="17" rx="6.5" ry="3" transform="rotate(-28 68 17)" />
        <ellipse cx="44" cy="9" rx="6.8" ry="3" transform="rotate(-10 44 9)" />
        <ellipse cx="32" cy="66" rx="7" ry="3.2" transform="rotate(62 32 66)" />
        <ellipse cx="16" cy="58" rx="6.5" ry="3" transform="rotate(118 16 58)" />
        <ellipse cx="33" cy="104" rx="7" ry="3.2" transform="rotate(62 33 104)" />
        <ellipse cx="17" cy="96" rx="6.5" ry="3" transform="rotate(118 17 96)" />
        <ellipse cx="10" cy="124" rx="6.8" ry="3" transform="rotate(80 10 124)" />
        {/* 末端小花苞 */}
        <circle cx="132" cy="9" r="2.6" />
        <circle cx="11" cy="132" r="2.6" />
      </g>
    </svg>
  );
}

// 帶對角藤蔓的卡片（左上 + 右下呼應）
function SprigCard({ children, corners = ['top-left', 'bottom-right'], style, ...rest }) {
  return (
    <Card {...rest} style={{ position: 'relative', overflow: 'hidden', ...style }}>
      {corners.map((c) => <CornerVine key={c} position={c} />)}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </Card>
  );
}

export default function InsightDetail() {
  const q = new URLSearchParams(useLocation().search);
  // 優先用編碼後的 c 參數（生日不明碼）；沒有才退回舊的 type/d/t 明碼
  const decoded = q.get('c') ? decodeInsight(q.get('c')) : null;
  const type = decoded ? decoded.type : q.get('type');
  const d = decoded ? decoded.d : q.get('d');
  const t = decoded ? (decoded.t == null ? null : String(decoded.t)) : q.get('t');
  const lite = q.get('preview') === '1'; // 後台 iframe 預覽：關掉動畫與毛玻璃以求流暢
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [formImages, setFormImages] = useState({});
  const [social, setSocial] = useState({});
  const [theme, setTheme] = useState({ primary: 'var(--brand-primary)', secondary: 'var(--brand-secondary)' });
  const [ready, setReady] = useState(false);
  useEffect(() => {
    fetch(`${API_BASE}/api/settings/social`).then((r) => (r.ok ? r.json() : {})).then(setSocial).catch(() => {});
    fetch(`${API_BASE}/api/settings/theme`).then((r) => (r.ok ? r.json() : {}))
      .then((d) => { if (d && d.primary) setTheme({ primary: d.primary, secondary: d.secondary || 'var(--brand-secondary)' }); }).catch(() => {});
  }, []);
  useEffect(() => {
    let alive = true;
    const safety = setTimeout(() => { if (alive) setReady(true); }, 6000); // 最久等 6 秒就先呈現
    fetch(`${API_BASE}/api/settings/form-images`)
      .then((r) => (r.ok ? r.json() : {}))
      .then(async (imgs) => {
        if (!alive) return;
        setFormImages(imgs || {});
        // 預覽模式不做預載（後台 iframe 求快）；正式頁才預載避免「icon 閃一下變圖」
        if (lite) return;
        const urls = Object.values(imgs || {}).filter((v) => typeof v === 'string' && /^(https?:)?\//.test(v));
        await Promise.all(urls.map((u) => new Promise((res) => {
          const im = new Image(); im.onload = res; im.onerror = res; im.src = u;
        })));
      })
      .catch(() => {})
      .finally(() => { if (alive) { clearTimeout(safety); setReady(true); } });
    return () => { alive = false; clearTimeout(safety); };
  }, []);

  const data = useMemo(() => {
    if (!d) return null;
    const day = dayjs(d);
    if (!day.isValid()) return null;
    try {
      if (type === 'wuxing') {
        if (t == null) return null;
        const w = calculateWuxing(day.year(), day.month() + 1, day.date(), parseInt(t));
        return { type, w, sup: getWuxingRecommendation(w, 'supplement'), str: getWuxingRecommendation(w, 'strengthen') };
      }
      if (type === 'numerology') {
        const num = calculateNumerology(day.year(), day.month() + 1, day.date());
        return { type, num, rec: getNumerologyRecommendation(num) };
      }
    } catch (e) { return null; }
    return null;
  }, [type, d, t]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    message.success('已複製分享連結');
  };
  const shareLink = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: '星河耀眼 — 我的命理分析', url: window.location.href }); } catch (e) { /* cancelled */ }
    } else { copyLink(); }
  };

  // 背景圖：優先用 insight 專屬背景，否則退回客製頁共用背景
  const bgImage = formImages[isMobile ? 'insight_bg_mobile' : 'insight_bg_desktop'] || formImages.insight_bg
    || formImages[isMobile ? 'bg_mobile' : 'bg_desktop'] || formImages.hero || null;

  // 五行小圖示：有後台上傳的元素圖就用圖，否則退回 emoji
  const EL_KEY = { '木': 'wood', '火': 'fire', '土': 'earth', '金': 'metal', '水': 'water' };
  const elIcon = (el, size = 18) => {
    const img = formImages[`wuxing_${EL_KEY[el]}`];
    if (img) return <img src={img} alt={el}
      style={{ width: size, height: size, objectFit: 'contain', verticalAlign: '-0.18em', display: 'inline-block' }}
      onError={(e) => { e.target.style.display = 'none'; }} />;
    return <span style={{ fontSize: size }}>{WUXING_EMOJI[el]}</span>;
  };

  const shell = (children) => (
    <ConfigProvider theme={{ token: { colorPrimary: theme.primary, colorTextSecondary: '#595959', borderRadius: 8 } }}>
      {/* 底色：淡紫漸層，避免太白；有設背景圖再疊上去 */}
      <style>{`
        @keyframes insightFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
        @keyframes insightShadow { 0%,100% { transform: scaleX(1); opacity: 0.85; } 50% { transform: scaleX(0.82); opacity: 0.55; } }
        @keyframes radarBreath { 0%,100% { opacity: 0.92; } 50% { opacity: 1; } }
        @keyframes radarPing { 0% { r: 4; opacity: 0.5; } 70%,100% { r: 11; opacity: 0; } }
        @keyframes radarSheen { 0% { opacity: 0.15; } 50% { opacity: 0.4; } 100% { opacity: 0.15; } }
        @media (prefers-reduced-motion: reduce) { [style*="insightFloat"], [style*="insightShadow"], [style*="radarBreath"], [style*="radarPing"], [style*="radarSheen"] { animation: none !important; } }
        /* 卡片做成「霧面玻璃」：半透明讓背景透出，blur 保住可讀性 */
        .insight-root .ant-card { background: rgba(255,255,255,0.4); -webkit-backdrop-filter: blur(2.5px); backdrop-filter: blur(2.5px); border: 1px solid rgba(255,255,255,0.55); box-shadow: 0 6px 22px rgba(34,41,87,0.08); }
        /* FAQ 折疊面板：展開內容給乾淨底色，確保文字清楚 */
        .insight-root .ant-collapse { background: rgba(255,255,255,0.55); border-radius: 10px; overflow: hidden; }
        .insight-root .ant-collapse-content { background: rgba(255,255,255,0.92); }
        .insight-root .ant-collapse-header { background: rgba(255,255,255,0.4); }
        /* 預覽模式（後台 iframe）：關掉所有動畫與毛玻璃，避免卡頓 */
        .insight-lite [style*="insightFloat"], .insight-lite [style*="insightShadow"],
        .insight-lite [style*="radarBreath"], .insight-lite [style*="radarPing"], .insight-lite [style*="radarSheen"] { animation: none !important; }
        .insight-lite .ant-card { -webkit-backdrop-filter: none !important; backdrop-filter: none !important; background: rgba(255,255,255,0.9) !important; }
        .insight-lite .ant-collapse, .insight-lite .ant-collapse-content, .insight-lite .ant-collapse-header { -webkit-backdrop-filter: none !important; backdrop-filter: none !important; }
        /* SVG 內寫死的品牌色 → 跟著主色/副色（CSS 可覆寫 SVG presentation 屬性） */
        .insight-root [fill="#222957"] { fill: var(--brand-primary); }
        .insight-root [stroke="#222957"] { stroke: var(--brand-primary); }
        .insight-root [stop-color="#949DD6"] { stop-color: var(--brand-secondary); }
        .insight-root [stop-color="#6B77C7"] { stop-color: var(--brand-primary); }
        .insight-root [flood-color="#6B77C7"] { flood-color: var(--brand-primary); }
      `}</style>
      <div className={lite ? 'insight-root insight-lite' : 'insight-root'} style={{ minHeight: '100vh', position: 'relative', paddingBottom: 40, background: 'linear-gradient(180deg, #EDEEF7 0%, #F9F9FC 35%, #fafafa 100%)', '--brand-primary': theme.primary, '--brand-secondary': theme.secondary }}>
        {bgImage && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
            backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center',
            opacity: 0.4,
          }} />
        )}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* 命理詳細頁封面：整張滿版置頂，圖內已含「星河耀眼」字樣，取代原本的 logo 標頭 */}
          {formImages.insight_hero && (
            <img src={formImages.insight_hero} alt="星河耀眼" style={{ width: '100%', display: 'block', maxHeight: isMobile ? 280 : 360, objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <div style={{ maxWidth: 600, margin: '0 auto', padding: isMobile ? 16 : 24 }}>
            {children}
          </div>
          {/* 品牌頁尾 */}
          <div style={{ textAlign: 'center', padding: '12px 16px 0', color: 'var(--brand-secondary)' }}>
            {(social.facebook || social.instagram || social.line) && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 10 }}>
                {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }} aria-label="Facebook"><FacebookFilled style={{ fontSize: 24 }} /></a>}
                {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }} aria-label="Instagram"><InstagramFilled style={{ fontSize: 24 }} /></a>}
                {social.line && <a href={social.line} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 8px', borderRadius: 12, background: '#06C755', color: '#fff', fontSize: 12, fontWeight: 700 }} aria-label="LINE">LINE</a>}
              </div>
            )}
            <StarFilled style={{ fontSize: 16 }} />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>© 星河耀眼 ShiningStars · 溫柔陪伴每一個您</div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );

  if (!ready) {
    // 流星雨：起點偏右上，往左下流
    const meteors = Array.from({ length: 16 }).map((_, i) => ({
      i,
      left: 28 + ((i * 37) % 85),      // 28% ~ 110%
      top: -18 + ((i * 23) % 55),      // -18% ~ 37%
      len: 60 + ((i * 17) % 70),       // 60 ~ 130px
      dur: 1.1 + ((i * 13) % 90) / 100, // 1.1 ~ 2.0s
      delay: ((i * 29) % 220) / 100,    // 0 ~ 2.2s
    }));
    return (
      <div style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, #1A2046 0%, #252D61 55%, #141834 100%)' }}>
        <style>{`
          @keyframes meteorFall { 0% { transform: translate(0,0) rotate(45deg); opacity: 0; } 8% { opacity: 1; } 85% { opacity: 1; } 100% { transform: translate(-1200px, 1200px) rotate(45deg); opacity: 0; } }
          @keyframes starGlow { 0%,100% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(255,213,74,.9)) drop-shadow(0 0 18px rgba(255,200,40,.5)); } 50% { transform: scale(1.12); filter: drop-shadow(0 0 14px rgba(255,213,74,1)) drop-shadow(0 0 32px rgba(255,200,40,.7)); } }
        `}</style>
        {meteors.map((m) => (
          <span key={m.i} style={{ position: 'absolute', top: `${m.top}%`, left: `${m.left}%`, width: 2, height: m.len, borderRadius: 2, opacity: 0, transform: 'rotate(45deg)', background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(229,231,244,0.95) 100%)', animation: `meteorFall ${m.dur}s linear ${m.delay}s infinite backwards` }} />
        ))}
        <StarFilled style={{ fontSize: 50, color: '#FFD54A', position: 'relative', zIndex: 1, animation: 'starGlow 1.8s ease-in-out infinite' }} />
        <div style={{ marginTop: 18, color: '#E5E7F4', fontSize: 13, letterSpacing: 1, position: 'relative', zIndex: 1 }}>正在為您準備分析…</div>
      </div>
    );
  }

  if (!data) {
    return shell(
      <Result status="warning" title="連結資料不完整"
        subTitle="這個分析連結缺少必要的生日資訊，請回到客製頁重新產生。"
        extra={<Link to="/custom"><Button type="primary">前往客製手鏈</Button></Link>} />
    );
  }

  const shareButtons = (
    <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
      <Button block size="large" icon={<ShareAltOutlined />} onClick={shareLink}>分享</Button>
      <Button block size="large" icon={<CopyOutlined />} onClick={copyLink}>複製連結</Button>
    </div>
  );
  const ctaButton = (
    <Link to="/custom">
      <Button type="primary" block size="large" style={{ height: 48, marginTop: 12 }}>為自己客製一條專屬手鏈 ✨</Button>
    </Link>
  );

  // 「關於這份分析」標題：左側放品牌 logo
  const aboutHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      {formImages.logo ? (
        <img src={formImages.logo} alt="星河耀眼" style={{ height: 52, objectFit: 'contain', flexShrink: 0 }}
          onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        <StarFilled style={{ fontSize: 32, color: 'var(--brand-primary)', flexShrink: 0 }} />
      )}
      <Text strong style={{ fontSize: 15 }}>關於這份分析</Text>
    </div>
  );

  // ===== 五行 =====
  if (data.type === 'wuxing') {
    const { w, sup, str } = data;
    const { bazi, scores, analysis } = w;
    // 日主（日元）= 日柱天干；身強身弱由「同我 + 生我」的力量判斷
    const dayStem = bazi.day.tiangan;
    const dayEl = TIANGAN_EL[dayStem];
    const dayYY = TIANGAN_YINYANG[dayStem];
    const supportScore = (scores[dayEl] || 0) + (scores[EL_GEN[dayEl]] || 0);
    const isStrong = supportScore >= 8; // 總分 16
    const useElements = (isStrong ? str : sup).recommendations.filter((r) => r.role === 'primary' || r.role === 'generate');
    return shell(
      <>
        <SprigCard style={{ marginBottom: 16, background: 'rgba(240,241,248,0.42)', border: '1px solid rgba(190,195,230,0.55)' }}>
          {aboutHeader}
          <Paragraph style={{ fontSize: 14, marginBottom: 0, color: '#595959' }}>
            五行分析以您的出生年月日時換算成八字（天干地支），統計金、木、水、火、土五種能量的強弱分布。
            五行之間有「相生」（彼此滋養）與「相剋」（互相制約）的循環，當某一行過弱或過旺，
            就能透過顏色、配飾等方式溫柔地補足或平衡——對您最有幫助的那一行，就稱為「喜用神」。
            這份報告會帶您看懂自己的能量分布，並給出對應的開運色彩。
          </Paragraph>
        </SprigCard>

        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>五行能量分佈</Text>
          <WuxingRadar scores={scores} icons={Object.fromEntries(['木', '火', '土', '金', '水'].map((el) => [el, formImages[`wuxing_${EL_KEY[el]}`]]).filter(([, u]) => u))} />
          <Divider style={{ margin: '12px 0' }}>四柱八字</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, textAlign: 'center' }}>
            {[['年柱', bazi.year], ['月柱', bazi.month], ['日柱', bazi.day], ['時柱', bazi.hour]].map(([l, p]) => (
              <div key={l} style={{ background: l === '日柱' ? '#E5E7F5' : '#F4F5FB', borderRadius: 8, padding: '10px 6px', border: l === '日柱' ? '1px solid var(--brand-secondary)' : 'none' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{l}{l === '日柱' ? '·日主' : ''}</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--brand-primary)', marginTop: 4 }}>{p.full}</div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{TIANGAN_EL[p.tiangan]}·{DIZHI_EL[p.dizhi]}</div>
              </div>
            ))}
          </div>
          <Divider style={{ margin: '12px 0' }} />
          <Text strong style={{ display: 'block', marginBottom: 8 }}>能量逐項</Text>
          {['木', '火', '土', '金', '水'].map((el) => (
            <div key={el} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {elIcon(el, 20)}<Text strong>{el}</Text>
                <Text type="secondary" style={{ marginLeft: 'auto' }}>{scores[el]} 分</Text>
              </div>
              <Progress percent={(scores[el] / 16) * 100} strokeColor={WUXING_COLORS[el]} showInfo={false} size="small" />
            </div>
          ))}
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>日主與旺衰</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--brand-primary)' }}>{dayStem}</div>
            <div>
              <Tag color={WUXING_COLORS[dayEl]}>{dayYY}{dayEl}</Tag>
              <Tag color={isStrong ? 'volcano' : 'blue'}>{isStrong ? '日主偏旺' : '日主偏弱'}</Tag>
            </div>
          </div>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
            「日主」是日柱的天干，代表您的核心自我——您是{dayYY}性的{dayEl}（{WUXING_COLOR_MAP[dayEl]?.trait}）。
            以「同我＋生我」的力量來看，您的日主{isStrong
              ? '偏旺，氣勢充足。宜順勢用「我生／我剋」之五行來抒發與發揮，避免再過度增旺。'
              : '偏弱，較需支援。宜用「生我（印）＋同我（比劫）」的五行來扶助，讓自己更有底氣。'}
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: 16, background: 'rgba(240,241,248,0.42)', border: '1px solid rgba(190,195,230,0.55)' }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>溫柔解讀</Text>
          <Paragraph style={{ fontSize: 15, marginBottom: 0 }}>
            {analysis.strongest.length > 0 && <>
              <Text>您天生擁有豐沛的</Text>
              {analysis.strongest.map((el) => <Tag key={el} color={WUXING_COLORS[el]} style={{ margin: '0 4px' }}>{elIcon(el, 14)} {el}</Tag>)}
              <Text>元素能量，代表著{analysis.strongest.map((e) => WUXING_COLOR_MAP[e]?.trait).join('與')}。</Text><br /><br />
            </>}
            {analysis.weakest.length > 0 && <>
              <Text>而</Text>
              {analysis.weakest.map((el) => <Tag key={el} color={WUXING_COLORS[el]} style={{ margin: '0 4px' }}>{elIcon(el, 14)} {el}</Tag>)}
              <Text>元素稍弱，可透過水晶溫柔補足。</Text>
            </>}
            {analysis.strongest.length === 0 && analysis.weakest.length === 0 && <Text>您的五行能量相當平衡，可依喜好選擇想加強的方向。</Text>}
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>喜用神 · 開運色彩</Text>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
            喜用神是對您最有幫助的五行。補益時會一併納入「相生」（生我者，如木生火）讓補強更穩；
            而「相剋」的元素會削弱目標，列為宜避免。
          </Paragraph>
          {[{ label: '補足較弱的元素', recs: sup.recommendations, avoid: sup.avoid }, { label: '強化突出的優勢', recs: str.recommendations, avoid: str.avoid }].map((opt) => (
            <div key={opt.label} style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 14 }}>{opt.label}</Text>
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {opt.recs.map((r) => (
                  <Tag key={r.element} color={WUXING_COLORS[r.element]}>
                    {elIcon(r.element, 16)} {r.element}{r.role === 'generate' ? ' · 相生' : ''}
                  </Tag>
                ))}
              </div>
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {opt.recs.flatMap((r) => r.colors).map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)}
              </div>
              {(opt.avoid || []).length > 0 && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                  相剋宜避免：{opt.avoid.map((a, i) => <span key={a.element}>{i > 0 && '、'}{elIcon(a.element, 13)}{a.element}</span>)}
                </Text>
              )}
            </div>
          ))}
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>開運全方位建議</Text>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 10 }}>
            依您的喜用神（{useElements.map((r) => r.element).join('、')}）延伸的生活開運方向：
          </Paragraph>
          {useElements.map((r) => {
            const info = WUXING_INFO[r.element] || {};
            return (
              <div key={r.element} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px dashed #f0f0f0' }}>
                <Tag color={WUXING_COLORS[r.element]} style={{ marginBottom: 6 }}>{elIcon(r.element, 16)} {r.element}{r.role === 'generate' ? ' · 相生' : ''}</Tag>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 13 }}>
                  <div><Text type="secondary">開運色：</Text>{(r.colors || []).join('、')}</div>
                  <div><Text type="secondary">有利方位：</Text>{info.dir}</div>
                  <div><Text type="secondary">幸運數字：</Text>{info.num}</div>
                  <div><Text type="secondary">有利季節：</Text>{info.season}</div>
                  <div style={{ gridColumn: '1 / -1' }}><Text type="secondary">適合領域：</Text>{info.career}</div>
                </div>
              </div>
            );
          })}
          <Text type="secondary" style={{ fontSize: 12 }}>＊以上為傳統五行對應的生活開運參考，非絕對；本表單聚焦以水晶顏色為您補強能量。</Text>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>五行各元素的意義</Text>
          {['木', '火', '土', '金', '水'].map((el) => {
            const elImg = formImages[`wuxing_${({ '木': 'wood', '火': 'fire', '土': 'earth', '金': 'metal', '水': 'water' })[el]}`];
            return (
              <div key={el} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                {elImg ? (
                  <img src={elImg} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <span style={{ fontSize: 38, lineHeight: 1, flexShrink: 0 }}>{WUXING_EMOJI[el]}</span>
                )}
                <div>
                  <Tag color={WUXING_COLORS[el]} style={{ margin: 0 }}>{el}</Tag>
                  <Text type="secondary" style={{ fontSize: 13, marginLeft: 6 }}>
                    {WUXING_COLOR_MAP[el]?.trait}（您的能量 {scores[el]} 分）
                  </Text>
                </div>
              </div>
            );
          })}
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>五行生剋關係</Text>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>相生（循環滋養）</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginBottom: 12 }}>
            {['木', '火', '土', '金', '水', '木'].map((el, i) => (
              <React.Fragment key={i}>
                <Tag color={WUXING_COLORS[el]} style={{ margin: 0 }}>{elIcon(el, 14)} {el}</Tag>
                {i < 5 && <span style={{ color: '#bbb' }}>→</span>}
              </React.Fragment>
            ))}
          </div>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>相剋（互相制約）</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[['木', '土'], ['火', '金'], ['土', '水'], ['金', '木'], ['水', '火']].map(([a, b]) => (
              <Tag key={a} style={{ margin: 0 }}>{elIcon(a, 13)}{a} ⊸ {elIcon(b, 13)}{b}</Tag>
            ))}
          </div>
        </Card>

        <Collapse style={{ marginBottom: 16 }} items={[
          { key: '1', label: '什麼是五行分析？', children: <Text style={{ fontSize: 13.5, color: '#2B3471' }}>依出生年月日時的天干地支，計算金、木、水、火、土五種元素的強弱分布，判斷五行是否平衡，並推算對您有利的「喜用神」。</Text> },
          { key: '2', label: '喜用神是什麼？', children: <Text style={{ fontSize: 13.5, color: '#2B3471' }}>指對命主有利的五行元素——能補足不足、平衡過旺的那一行，可用於指導穿戴顏色與配飾選擇。</Text> },
          { key: '3', label: '五行調和建議有哪些？', children: <Text style={{ fontSize: 13.5, color: '#2B3471' }}>除了開運顏色，傳統上還延伸到有利方位、幸運數字、適合職業、補益飲食與有利季節等。</Text> },
        ]} />

        {shareButtons}
        {ctaButton}
      </>
    );
  }

  // ===== 生命靈數 =====
  const { num, rec } = data;
  const { digits, coreNumbers, missingInsights, lines } = num;
  const lp = coreNumbers.lifePath;
  const missingColors = rec.missingRecommendations?.flatMap((m) => m.colors) || [];
  const meaning = NUMBER_MEANINGS[lp.value] || {};
  // 命數形象圖：優先用後台切好的單圖，否則退回九宮格等分切割
  const numImg = (() => {
    const box = { width: isMobile ? 170 : 200, aspectRatio: '2 / 3', borderRadius: 14, backgroundRepeat: 'no-repeat', boxShadow: '0 18px 36px -10px rgba(34,41,87,0.38), 0 6px 14px -6px rgba(34,41,87,0.25)' };
    const per = formImages[`numerology_${lp.value}`];
    let inner = null;
    if (per) inner = <div role="img" aria-label={lp.summary} style={{ ...box, backgroundImage: `url(${per})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />;
    else if (formImages.numerology_grid && lp.value >= 1 && lp.value <= 9) {
      const idx = lp.value - 1, col = idx % 3, row = Math.floor(idx / 3);
      inner = <div role="img" aria-label={lp.summary} style={{ ...box, backgroundImage: `url(${formImages.numerology_grid})`, backgroundSize: '300% 300%', backgroundPosition: `${col * 50}% ${row * 50}%` }} />;
    }
    if (!inner) return null;
    return (
      <div style={{ position: 'relative', width: 'fit-content', margin: '0 auto 16px', paddingTop: 6 }}>
        {/* 背後光暈，營造浮起的層次 */}
        <div style={{ position: 'absolute', inset: '-12% -16% -2%', background: 'radial-gradient(60% 55% at 50% 42%, rgba(107,119,199,0.28), rgba(107,119,199,0) 70%)', filter: 'blur(8px)', zIndex: 0, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, animation: 'insightFloat 4.5s ease-in-out infinite' }}>{inner}</div>
        {/* 地面投影，隨主體浮動 */}
        <div style={{ width: '62%', height: 14, margin: '10px auto 0', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(34,41,87,0.22), rgba(34,41,87,0) 70%)', animation: 'insightShadow 4.5s ease-in-out infinite' }} />
      </div>
    );
  })();
  return shell(
    <>
      <Card style={{ marginBottom: 16, background: 'rgba(240,241,248,0.42)', border: '1px solid rgba(190,195,230,0.55)' }}>
        {aboutHeader}
        <Paragraph style={{ fontSize: 14, marginBottom: 0, color: '#595959' }}>
          生命靈數源自您的西元生日，把年月日各數字相加、化約到 1–9（或大師數），得出代表您核心特質的「命數」。
          再把生日數字排進「九宮格」，看哪些能量充足、哪些缺乏，並從中讀出您的天賦連線與課題。
          這份報告會帶您認識自己的命數性格，並給出能溫柔加分的開運色彩。
        </Paragraph>
      </Card>

      <Card style={{ marginBottom: 16, textAlign: 'center' }}>
        {numImg}
        <div style={{ fontSize: 48, fontWeight: 800, color: 'var(--brand-primary)' }}>{lp.value}</div>
        <Title level={5} style={{ margin: '8px 0 4px' }}>命數 {lp.value} — {lp.summary}</Title>
        {lp.isMaster && <Tag color="gold">大師數 {lp.masterNumber}</Tag>}
        <Paragraph style={{ color: '#595959', marginTop: 8, marginBottom: 8 }}>{lp.strengths}</Paragraph>
        <div>{lp.keywords.map((k) => <Tag key={k} color="geekblue">{k}</Tag>)}</div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>四大核心數字</Text>
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 10 }}>
          命數是您的人生主旋律；生日、月份、年份能量則像三種伴奏，一起構成完整的您。
        </Paragraph>
        {[coreNumbers.lifePath, coreNumbers.birthday, coreNumbers.month, coreNumbers.year].map((core) => (
          <div key={core.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(240,241,248,0.42)', border: '1px solid rgba(190,195,230,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--brand-primary)', flexShrink: 0 }}>
              {core.isMaster ? core.masterNumber : core.value}
            </div>
            <div>
              <Text strong style={{ fontSize: 13 }}>{core.label} · {core.value} {core.summary}</Text>
              <div><Text type="secondary" style={{ fontSize: 12 }}>{core.strengths}</Text></div>
            </div>
          </div>
        ))}
      </Card>

      <Card style={{ marginBottom: 16, background: 'rgba(240,241,248,0.42)', border: '1px solid rgba(190,195,230,0.55)' }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>命數 {lp.value} 深度解析</Text>
        <Paragraph style={{ fontSize: 14, marginBottom: 6, color: '#595959' }}><b>天賦優勢：</b>{meaning.strengths || lp.strengths}</Paragraph>
        {meaning.watchouts && <Paragraph style={{ fontSize: 14, marginBottom: 6, color: '#595959' }}><b>成長提醒：</b>{meaning.watchouts}</Paragraph>}
        {meaning.missingAdvice && <Paragraph style={{ fontSize: 14, marginBottom: 0, color: '#595959' }}><b>人生功課：</b>{meaning.missingAdvice}</Paragraph>}
      </Card>

      {digits.dominantNumbers.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>您的天賦數</Text>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 10 }}>
            把生日數字（含命數等核心數）排進九宮格後，<Text strong style={{ color: 'var(--brand-primary)' }}>同一個數字出現 2 次以上</Text>就是您的天賦數——出現越多次，這份能量越鮮明（您可以在下方九宮格看到它重複出現的位置）。
          </Paragraph>
          {digits.dominantNumbers.map((dn) => {
            const m = NUMBER_MEANINGS[dn] || {};
            return (
              <div key={dn} style={{ marginBottom: 8 }}>
                <Tag color="geekblue">{dn} · {m.title}（出現 {digits.counts[dn]} 次）</Tag>
                <Text type="secondary" style={{ fontSize: 13 }}>{m.strengths}</Text>
              </div>
            );
          })}
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>數字能量九宮格</Text>
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
          每個數字在您生日中出現幾次，就用幾層圈圈起來——圈越多層，這份能量越強；空白格是「缺數」課題。三格連成一線即為「天賦連線」（紫線標示）。
        </Paragraph>
        <div style={{ position: 'relative' }}>
          {lines.length > 0 && (
            <svg viewBox="0 0 3 3" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 5, pointerEvents: 'none' }}>
              <defs>
                <linearGradient id="gridLine" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#949DD6" />
                  <stop offset="100%" stopColor="#eb6fb0" />
                </linearGradient>
              </defs>
              {lines.map((l) => {
                const ds = (String(l.combination).match(/[1-9]/g) || []).map(Number);
                if (ds.length < 2) return null;
                const pts = ds.map((dd) => `${((dd - 1) % 3) + 0.5},${Math.floor((dd - 1) / 3) + 0.5}`).join(' ');
                return <polyline key={l.combination} points={pts} fill="none" stroke="url(#gridLine)" strokeWidth={0.055} strokeOpacity={0.7} strokeLinecap="round" strokeLinejoin="round" />;
              })}
            </svg>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, position: 'relative', zIndex: 1 }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((dd) => {
              const count = digits.counts[dd];
              const isMissing = count === 0;
              const isLifePath = dd === lp.value;
              return (
                <div key={dd} style={{ minHeight: 86, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 6px', borderRadius: 10, background: isLifePath ? '#F4F5FB' : isMissing ? '#fafafa' : '#fff', border: isLifePath ? '2px solid var(--brand-primary)' : isMissing ? '1px dashed #D4D8EE' : '1px solid #f0f0f0', boxShadow: isMissing ? 'none' : '0 1px 4px rgba(34,41,87,0.06)' }}>
                  {isMissing ? (
                    <>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#D7DAEF' }}>{dd}</div>
                      <span style={{ fontSize: 12, color: '#AEB5E0' }}>缺</span>
                    </>
                  ) : (
                    <>
                      {(() => {
                        // 出現幾次 = 幾層圈（封頂 3 層，超過再用 ×N 補充）
                        const rings = Math.min(count, 3);
                        const baseD = 32, step = 9;
                        const outer = baseD + (rings >= 2 ? (rings - 1) * 2 * step : 0);
                        return (
                          <div style={{ position: 'relative', width: outer, height: outer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {count >= 2 && Array.from({ length: rings }).map((_, r) => {
                              const d = baseD + r * 2 * step;
                              return <span key={r} style={{ position: 'absolute', width: d, height: d, borderRadius: '50%', border: '1.5px solid var(--brand-primary)', opacity: 0.55 - r * 0.13 }} />;
                            })}
                            <span style={{ fontSize: 22, fontWeight: 800, color: '#374390', position: 'relative', zIndex: 1 }}>{dd}</span>
                          </div>
                        );
                      })()}
                      {count > 3 && <span style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>出現 {count} 次</span>}
                      {isLifePath && <span style={{ fontSize: 12, color: 'var(--brand-primary)', marginTop: 2 }}>命數</span>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {(lines.length > 0 || missingInsights.length > 0) && (
        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>能量解讀</Text>
          {lines.map((l) => (
            <div key={l.combination} style={{ marginBottom: 8 }}>
              <Tag color="blue">{l.label}</Tag>
              <Text type="secondary" style={{ fontSize: 13 }}>{l.description}</Text>
            </div>
          ))}
          {lines.length > 0 && missingInsights.length > 0 && <Divider style={{ margin: '8px 0' }} />}
          {missingInsights.map((m) => (
            <div key={m.digit} style={{ marginBottom: 8 }}>
              <Text strong>缺數 {m.digit} — {m.title}</Text><br />
              <Text type="secondary" style={{ fontSize: 13 }}>{m.suggestion}</Text>
            </div>
          ))}
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 10 }}>開運色彩</Text>
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 14 }}>強化命數特質</Text>
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(rec.lifePath?.colors || []).map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)}
          </div>
        </div>
        <div>
          <Text strong style={{ fontSize: 14 }}>補足缺數能量</Text>
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {missingColors.length > 0 ? missingColors.map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)
              : <Text type="secondary" style={{ fontSize: 13 }}>您的數字能量已很完整</Text>}
          </div>
        </div>
      </Card>

      {shareButtons}
      {ctaButton}
    </>
  );
}
